import React from 'react';
import { CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { OneCSync_Response } from '../../types/onec-sync.types';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

interface SyncResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  syncResult: OneCSync_Response | null;
  isLoading: boolean;
}

export function SyncResultsDialog({
  open,
  onOpenChange,
  syncResult,
  isLoading,
}: SyncResultsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Синхронизация справочников из 1С</DialogTitle>
          <DialogDescription>
            Результаты синхронизации подразделений, должностей, графиков и сотрудников
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex flex-col items-center justify-center p-12">
            <Clock className="h-16 w-16 animate-spin text-blue-500 mb-4" />
            <p className="text-lg font-medium">Синхронизация...</p>
            <p className="text-sm text-muted-foreground mt-2">
              Ожидание ответа от 1С системы
            </p>
          </div>
        )}

        {!isLoading && syncResult && (
          <div className="space-y-6 py-4">
            {/* Success State */}
            {syncResult.status === 'ok' && (
              <div className="space-y-4">
                {/* Success Icon */}
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-6">
                    <CheckCircle2 className="h-16 w-16 text-green-600" />
                  </div>
                </div>

                {/* Success Message */}
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-green-600 mb-2">
                    Синхронизация успешно завершена
                  </h3>
                  <p className="text-muted-foreground">
                    Все справочники успешно синхронизированы с 1С
                  </p>
                </div>

                {/* Sync Timestamp */}
                <div className="rounded-lg border bg-muted/30 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Время синхронизации
                      </p>
                      <p className="text-lg font-mono">
                        {new Date(syncResult.syncTimestamp).toLocaleString('ru-RU', {
                          timeZone: APP_TIME_ZONE,
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="px-4 py-2 rounded-lg bg-green-100">
                      <p className="text-sm font-semibold text-green-700">Успешно</p>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <div className="rounded-lg border bg-blue-50 p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Что было синхронизировано:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>Подразделения организации</li>
                      <li>Должности и роли</li>
                      <li>Графики работы и шаблоны</li>
                      <li>Сотрудники и их назначения</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {syncResult.status === 'error' && (
              <div className="space-y-4">
                {/* Error Icon */}
                <div className="flex justify-center">
                  <div className="rounded-full bg-red-100 p-6">
                    <XCircle className="h-16 w-16 text-red-600" />
                  </div>
                </div>

                {/* Error Message */}
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-red-600 mb-2">
                    Ошибка синхронизации
                  </h3>
                  <p className="text-muted-foreground">
                    Синхронизация завершилась с ошибками
                  </p>
                </div>

                {/* Error Details */}
                <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900 mb-1">
                        Сообщение об ошибке:
                      </p>
                      <p className="text-sm text-red-800">{syncResult.error}</p>
                    </div>
                  </div>
                </div>

                {/* Support Info */}
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Рекомендации:</span>
                  </p>
                  <ul className="mt-2 text-sm text-muted-foreground space-y-1">
                    <li>• Проверьте настройки подключения к 1С</li>
                    <li>• Убедитесь, что 1С система доступна</li>
                    <li>• Проверьте права доступа пользователя 1С</li>
                    <li>• Обратитесь к администратору системы</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
